import { ThemeEvents } from '@theme/events';

class FpooAtcPrice extends HTMLElement {
  /** @type {Record<string, {default: string, plans: Record<string, {price: string, per_delivery: string}>}>} */
  #prices = {};
  #variantId = '';
  #available = true;
  #frame = 0;
  #writes = [];
  #backedOff = false;
  #abort = new AbortController();
  /** @type {MutationObserver | null} */
  #buttonObserver = null;
  /** @type {MutationObserver | null} */
  #widgetObserver = null;

  connectedCallback() {
    this.#prices = this.#readPriceIsland();
    this.#variantId = this.#form?.elements.id?.value ?? '';
    this.#available = this.#container?.dataset.themeAvailable !== 'false';

    const { signal } = this.#abort;

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    section?.addEventListener(ThemeEvents.cartError, this.#onCartError, { signal });

    const button = this.#button;
    if (button) {
      this.#buttonObserver = new MutationObserver(() => this.#schedule());
      this.#buttonObserver.observe(button, { childList: true, subtree: true, characterData: true, attributes: true });
    }

    if (Object.keys(this.#prices).length) this.#observeSubscriptionWidget(signal);
    this.#schedule();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#buttonObserver?.disconnect();
    this.#widgetObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  get #button() {
    return /** @type {HTMLButtonElement | null} */ (this.closest('button, [ref="addToCartButton"]')) ??
      /** @type {HTMLButtonElement | null} */ (this.parentElement?.closest('button') ?? null);
  }

  get #container() {
    return this.closest('add-to-cart-component');
  }

  get #form() {
    return /** @type {HTMLFormElement | null} */ (this.closest('form'));
  }

  #readPriceIsland() {
    const script = this.#container?.querySelector('[data-fpoo-atc-prices]') ??
      this.closest('product-form-component')?.querySelector('[data-fpoo-atc-prices]');
    if (!script?.textContent) return {};
    try {
      return JSON.parse(script.textContent);
    } catch {
      console.warn('[fpoo-atc] Could not parse the price data island.');
      return {};
    }
  }

  #observeSubscriptionWidget(signal) {
    const widget = document.querySelector('.loop-widget-container');

    if (widget) {
      this.#widgetObserver = new MutationObserver(() => this.#schedule());
      this.#widgetObserver.observe(widget, {
        attributes: true,
        attributeFilter: ['aria-checked', 'class'],
        subtree: true,
      });
    }

    for (const type of ['click', 'change']) {
      document.addEventListener(
        type,
        (event) => {
          if (/** @type {Element} */ (event.target)?.closest?.('.loop-widget-container')) this.#schedule();
        },
        { capture: true, signal }
      );
    }

    let last = this.#sellingPlanId;
    const deadline = performance.now() + 10000;
    const poll = () => {
      if (signal.aborted) return;
      const current = this.#sellingPlanId;
      if (current !== last) {
        last = current;
        this.#schedule();
      }
      if (performance.now() < deadline) requestAnimationFrame(poll);
    };
    requestAnimationFrame(poll);
  }

  get #sellingPlanId() {
    const input = /** @type {HTMLInputElement | undefined} */ (this.#form?.elements.namedItem('selling_plan'));
    return input?.value || '';
  }

  #onVariantUpdate = (event) => {
    this.#variantId = String(event.detail?.resource?.id ?? this.#form?.elements.id?.value ?? '');
    this.#available = this.#container?.dataset.themeAvailable !== 'false';
    this.#schedule();
  };

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#reconcile());
  }

  #onCartError = (event) => {
    requestAnimationFrame(() => {
      const errorEl = this.#form?.querySelector('[ref="addToCartTextError"]');
      if (!errorEl || errorEl.classList.contains('hidden')) return;

      const message = event.detail?.data?.message ?? '';
      // Shopify quotes the product/variant name; fall back to the page title.
      const quoted = /['"]([^'"]+)['"]/.exec(message)?.[1];
      const title = this.closest('.shopify-section, dialog')?.querySelector('h1')?.textContent?.trim();
      const name = quoted || title || 'this item';
      const text = `No additional stock available for ${name}.`;

      const textNode = [...errorEl.childNodes].reverse().find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
      if (textNode) {
        textNode.textContent = ` ${text}`;
      } else {
        errorEl.appendChild(document.createTextNode(` ${text}`));
      }
    });
  };

  #resolvePrice() {
    const variant = this.#prices[this.#variantId];
    if (!variant) return null;

    const plan = variant.plans?.[this.#sellingPlanId];
    return plan?.price ?? variant.default ?? null;
  }

  #reconcile() {
    const button = this.#button;
    if (!button) return;

    const now = performance.now();
    this.#writes = this.#writes.filter((t) => now - t < 1000);
    if (this.#writes.length > 20) {
      if (!this.#backedOff) {
        this.#backedOff = true;
        console.warn('[fpoo-atc] Too many DOM writes in one second; backing off.');
      }
      return;
    }

    this.#available = this.#container?.dataset.themeAvailable !== 'false';

    const price = this.#available ? this.#resolvePrice() : null;
    const shouldHidePrice = !this.#available;

    const label = button.querySelector('.add-to-cart-text');
    const template = button.querySelector('[data-fpoo-atc-label]');
    const canonical = template?.textContent?.trim() ?? '';

    const needsDisable = !this.#available && !button.disabled;
    const needsPrice = price != null && this.textContent?.trim() !== price;
    const needsHide = this.hasAttribute('hidden') !== shouldHidePrice;
    const needsLabel = !!label && !!canonical && label.innerHTML.trim() !== canonical;

    if (!needsDisable && !needsPrice && !needsHide && !needsLabel) return;

    this.#buttonObserver?.disconnect();

    if (needsDisable) button.disabled = true;
    if (needsLabel) label.innerHTML = canonical;
    if (needsHide) this.toggleAttribute('hidden', shouldHidePrice);
    if (needsPrice) this.textContent = price;

    this.#writes.push(now);
    this.#buttonObserver?.takeRecords();
    if (button.isConnected) {
      this.#buttonObserver?.observe(button, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
      });
    }
  }
}

if (!customElements.get('fpoo-atc-price')) {
  customElements.define('fpoo-atc-price', FpooAtcPrice);
}
